// A Call Sheet puzzle in progress, and finished, kept on this device.
//
// The same mechanism Running Order uses (CLAUDE.md: A puzzle is played once, and
// it is picked up where it was left): the guesses made so far are stored, and
// everything else on screen is worked out from them — how many faces are showing,
// whether the game is over, whether it was won.
//
// **Only the guesses are stored.** A guess is a title's kind and id, never the
// text that was typed: the same shape a recorded result takes, and the same
// reason — names are read from the refreshed title table, so nothing here can go
// stale or disagree with what the dropdown shows.
import { MAX_GUESSES } from './types.ts';

const KEY = 'cinemore-call-sheet';
const KEEP = 60;

// A guess is a title, or a skip. A skip is stored because it is a guess: it
// spends one, it reveals the next actor, and it has to come back with the rest
// when a half-played puzzle is reopened. It carries no id, because there is no
// title to remember — and that is what tells the two apart everywhere.
export interface TitleGuess {
  id: number;
  kind: 'movie' | 'tv';
}
export interface Skip { skipped: true }
export type Guess = TitleGuess | Skip;

export function isSkip(g: Guess): g is Skip {
  return (g as Skip).skipped === true;
}

export interface Progress {
  guesses: Guess[];
  at: number;        // last touched, for pruning
}

type Store = Record<string, Progress>;

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Store : {};
  } catch {
    // Private window, blocked site data, a hand-edited value: no saved game.
    return {};
  }
}

function write(store: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    // Blocked or full. The game plays perfectly well without being saved.
  }
}

// A saved game is only usable if it still fits the game it claims to be: at most
// three guesses, each a kind and an id. A puzzle can be regenerated before it
// joins the pool, and a player can edit their own storage.
export function usable(entry: unknown): Progress | null {
  if (!entry || typeof entry !== 'object') return null;
  const { guesses, at } = entry as Partial<Progress>;
  if (!Array.isArray(guesses) || guesses.length > MAX_GUESSES) return null;
  const fits = guesses.every((g) =>
    g && typeof g === 'object'
    && ((g as Skip).skipped === true
      || (typeof (g as TitleGuess).id === 'number'
        && ((g as TitleGuess).kind === 'movie' || (g as TitleGuess).kind === 'tv'))));
  if (!fits) return null;
  return { guesses: guesses as Guess[], at: typeof at === 'number' ? at : 0 };
}

export function pruned(store: Store, keep = KEEP): Store {
  const entries = Object.entries(store);
  if (entries.length <= keep) return store;
  entries.sort(([, a], [, b]) => b.at - a.at);
  return Object.fromEntries(entries.slice(0, keep));
}

export function loadProgress(number: number): Progress | null {
  return usable(read()[String(number)]);
}

export function saveProgress(number: number, guesses: Guess[]): void {
  const store = read();
  store[String(number)] = { guesses, at: Date.now() };
  write(pruned(store));
}
